/* oxlint-disable complexity */
import { useRef } from 'react';
import type React from 'react';

import { Separator } from '@change/ds-core-ui';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	useIsEndorsementsPetitionEnabled,
	useStickyBehavior,
	PublicFieldContextProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from '#src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwareness } from '#src/app/shared/components/membershipAwareness';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { PetitionStarter } from '../../components/PetitionStarter';
import { DecisionMakerSection } from '../DecisionMakerSection';
import { DescriptionContainer } from '../description';
import { EmbeddedSignForm } from '../embeddedSignForm';
import { EndorsementsContainer } from '../endorsements';
import { HeadingContainerMobile } from '../HeadingMobile';
import { HeadingMobileWithPhoto } from '../HeadingMobileWithPhoto';
import { HeadingWithPhoto } from '../HeadingWithPhoto';
import { MediaHitsContainer } from '../MediaHitsContainer';
import { MobileActionButtonContainer } from '../mobileActionButton';
import { MomentumBarExperimentContent } from '../momentumBar';
import { PetitionTimestamp } from '../PetitionTimestamp';
import { ReportAbuseContainer } from '../ReportAbuse';
import { SapCtaContainer } from '../SapCta';
import { ScrollTrackingWaypointContainer } from '../ScrollTrackingWaypoint';
import { SharePetition } from '../sharePetition/SharePetition';
import { SideContainer } from '../side';
import { SignatureStatsMobileContainer } from '../stats/signatureStats';
import { TagBreadcrumbs } from '../TagBreadcrumbs';
import { TheSupporters } from '../TheSupporters';
import {
	SupporterVoicesFeedProvider,
	useSupporterVoicesFeedContext,
} from '../TheSupporters/components/SupporterVoices/components/SupporterVoicesFeed/shared/context';
import { UkSurgingPetitionCardContainer } from '../UkSurgingPetitionCard';
import { UpdatesContainer } from '../Updates';

import { useMediaMentionsPlacementExperiment } from './hooks/useMediaMentionsPlacementExperiment';

// oxlint-disable-next-line max-lines-per-function
const MainContainerInner: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { showSupporterVoicesFeed },
	} = useSupporterVoicesFeedContext();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();

	const {
		experiment: mediaMentionsExperiment,
		hasMediaMentions,
		showAboveDecisionMakers: showMediaMentionsAboveDecisionMakers,
	} = useMediaMentionsPlacementExperiment();

	return (
		<PublicFieldContextProvider>
			<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
				<div
					className={cn('col-span-full hidden w-full md:row-start-1 md:block', {
						'lg:col-start-2 lg:-col-end-1': photo === null,
					})}
				>
					{photo === null ? null : <HeadingWithPhoto />}
				</div>

				<div
					className={cn('col-span-full max-md:px-0 md:col-span-5 md:container lg:col-start-2! lg:col-end-11!', {
						'md:row-start-2': photo !== null,
					})}
				>
					{photo === null ? <HeadingContainerMobile /> : <HeadingMobileWithPhoto />}

					<div className="p-4 md:p-0">
						<div className="hidden md:block">
							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}
							<PetitionStarter />
						</div>

						<div className="block md:hidden">
							<MobileActionButtonContainer sticky={false} variant="v1" />

							<AfterMobileActionButtonWaypoint />
							<SignatureStatsMobileContainer />

							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}

							<PetitionStarter />
							<EmbeddedSignForm />
						</div>

						<ScrollTrackingWaypointContainer name="scroll_past_body" />
						<AfterDescriptionWaypoint />
					</div>
				</div>
				{/* We need to modify the row-span to match the exact last row before the first full width module */}
				<div
					data-qa="second-column-container"
					className="h-0 md:col-start-6 md:col-end-9 md:row-span-3 md:row-start-1 md:mb-20 md:h-full lg:col-start-11 lg:col-end-15"
				>
					<div className="block md:hidden">
						<MobileActionButtonContainer sticky={true} variant="v1" />
					</div>
					<div
						data-qa="sticky-action-panel-container"
						className={cn(
							'relative top-6 right-0 z-12 mt-0 hidden md:mt-6! md:mr-6! md:block md:max-w-[350px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
							{
								'!sticky': canBeSticky,
							},
						)}
					>
						<div>
							<SideContainer containerRef={signatureBlockRef} />
						</div>
					</div>
				</div>

				{/* Must stay a single instance: dedup is per mount, so a second tracker halves every measured rate. */}
				{hasMediaMentions && <TrackExperimentExposure experiment={mediaMentionsExperiment} resetKeys={[petitionId]} />}

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SapCtaContainer />

					{/* Shares this cell rather than being its own Grid child: splitting it adds a row gap for control too. */}
					{showMediaMentionsAboveDecisionMakers && <MediaHitsContainer className="mb-20" />}

					<DecisionMakerSection />
				</div>

				<div
					className={cn('col-span-full', {
						// Mixed feed: full-width breakout for the wide card grid.
						'my-6 px-4 md:px-0': showSupporterVoicesFeed,
						// Legacy comments/videos fallback: centered in the page grid, matching the
						// other petition-body sections (updates, share, etc.).
						'container lg:col-start-2 lg:col-end-11': !showSupporterVoicesFeed,
					})}
				>
					<TheSupporters showSupporterVoicesFeed={showSupporterVoicesFeed} />
				</div>

				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwareness sourceLocation="petition_details" petitionId={petitionId} slug={slug} signed={signed} />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<ScrollTrackingWaypointContainer name="scroll_updates_section" />

					<Separator className="mt-14 mb-8 md:mt-20" />

					<UpdatesContainer />
				</div>

				{!showMediaMentionsAboveDecisionMakers && (
					<div className="col-span-full container lg:col-start-2 lg:col-end-11">
						<MediaHitsContainer className="mt-13 mb-20" />
					</div>
				)}

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SharePetition />

					<Separator className="mt-14 mb-6 md:mt-20" />
					<PetitionTimestamp />
					<Separator className="my-6" />
					<TagBreadcrumbs />
				</div>

				{/* UK "surging petition" cover card (petition_page_uk_surging_card experiment) — end of the
				    main content column, directly above the promoted petitions. Renders its own grid-item
				    wrapper only when the card actually shows; contributes nothing in every suppressed state. */}
				<UkSurgingPetitionCardContainer />
			</Grid>
		</PublicFieldContextProvider>
	);
};

// The feed provider holds the single supporter-voices feed instance so the page layout decision
// (showSupporterVoicesFeed, read in MainContainerInner) and the feed render (SupporterVoicesFeed,
// nested below) share one `useSupporterVideos` state. See CHANGE-94834.
export const MainContainer: React.FC = () => {
	return (
		<SupporterVoicesFeedProvider>
			<MainContainerInner />
		</SupporterVoicesFeedProvider>
	);
};
